import { useMutation, useQuery } from "react-query";
import { useStoresContext } from "../../../../../../../../context/StoresContext";
import api, { queryFetcher } from "../../../../../../../APIResolvers";
import { IListOfLocations, MarketsModalType } from "../formConfig";

export type MarketOption = "AUTOMATED" | "MANUAL";

export type TAllRegionsByCountryListResponse =
  | IListOfLocations[]
  | { message: string }
  | null
  | undefined;

export type TAllCountriesListResponse = IListOfLocations[] | null | undefined;

export type TAllMarketsListResponse = {
  markets: MarketsModalType[];
  selectedOption: MarketOption;
} | null;

interface IUseOperateMarkets {
  country: MarketsModalType["country"];
  onSuccessCallback?: () => void;
}

export interface IMarketsRequest {
  storeId: string;
}

export interface IAllRegionsByCountryListRequest extends IMarketsRequest {
  country: string;
}

export enum Methods {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
  DELETE = "DELETE",
}

interface IMarketsMutationRequest {
  method: Methods;
  market: MarketsModalType;
  selectedOption?: MarketOption;
}

export const useOperateMarkets = ({ country, onSuccessCallback }: IUseOperateMarkets) => {
  const { selectedStore } = useStoresContext();
  const storeId = selectedStore?.id || "";

  const selectedCountry: string = country?.length ? country?.[0]?.id : "";

  const {
    data: marketsData,
    refetch: refetchAllMarketsList,
    isLoading: isAllMarketsListLoading,
  } = useQuery<TAllMarketsListResponse>(["allMarketsList", storeId], () =>
    queryFetcher(api.marketsCost.ENDPOINTS.allMarketsList({ storeId })),
  );

  const allMarketsList = marketsData?.markets || null;
  const selectedOption = marketsData?.selectedOption || "AUTOMATED";

  // Get All Countries List
  const { data: allCountriesList, refetch: refetchAllCountriesList } =
    useQuery<TAllCountriesListResponse>(["allCountriesList", storeId], () =>
      queryFetcher(api.marketsCost.ENDPOINTS.allCountriesList({ storeId })),
    );

  // Get All Regions By Country List
  const { data: allRegionsByCountryList, refetch: refetchAllRegionsByCountryList } =
    useQuery<TAllRegionsByCountryListResponse>(
      ["allRegionsByCountryList", storeId, selectedCountry],
      () =>
        country?.length <= 1
          ? queryFetcher(
              api.marketsCost.ENDPOINTS.allRegionsByCountryList({
                storeId,
                country: selectedCountry,
              }),
            )
          : [
              {
                id: "All",
                title: "All",
              },
            ],
    );

  const {
    mutate: mutateMarketsList,
    isLoading: mutateMarketsListLoading,
    isError: isMutateMarketsListError,
    reset: resetMutateMarketsList,
  } = useMutation(
    ({ market, method, selectedOption: mutationSelectedOption }: IMarketsMutationRequest) =>
      queryFetcher(api.marketsCost.ENDPOINTS.createUpdateDeleteMarkets(), {
        method,
        body: JSON.stringify({
          storeId,
          market,
          selectedOption: mutationSelectedOption,
        }),
      }),

    {
      onSuccess: () => {
        refetchAllMarketsList();
        onSuccessCallback?.();
      },
    },
  );

  // Update Selected Option Only
  const updateSelectedOptionMutation = useMutation<
    unknown,
    unknown,
    { selectedOption: MarketOption }
  >(
    ({ selectedOption }) =>
      queryFetcher(`/api/market-cost-settings/selected-option`, {
        method: "POST",
        body: JSON.stringify({ storeId, selectedOption }),
      }),
    {
      onSuccess: () => {
        refetchAllMarketsList();
        if (onSuccessCallback) onSuccessCallback();
      },
    },
  );

  return {
    allMarketsList,
    refetchAllMarketsList,
    isAllMarketsListLoading,
    selectedOption,
    allCountriesList,
    refetchAllCountriesList,
    allRegionsByCountryList,
    refetchAllRegionsByCountryList,
    mutateMarketsList,
    mutateMarketsListLoading,
    isMutateMarketsListError,
    resetMutateMarketsList,
    updateSelectedOptionMutation,
  };
};
